import React, { useContext, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactSelect from 'react-select'
import { DataGrid } from '@mui/x-data-grid'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import ResponsiveAppBar from '../../components/Navbar'
import { generate, create, resolveProduct } from '../../services/invoice'
import { getList } from '../../services/admin'
import {
  getPricing,
  getPricingCategories,
  saveProductMarkup,
} from '../../services/customer'
import { update as updateProduct } from '../../services/product'
import { UserContext } from '../../context/UserProvider.jsx'

const App = () => {
  const navigate = useNavigate()
  const { token } = useContext(UserContext)
  const currentFile = useRef(null)
  const [productResolutions, setProductResolutions] = useState({})
  const [selectedFile, setSelectedFile] = useState(null)
  const [invoiceData, setInvoiceData] = useState({
    markupPercentage: 0,
    markupCost: 0,
    deliveryFeePercentage: 0,
    deliveryFeeCost: 0,
  })
  const [generatedInfo, setGeneratedInfo] = useState(null)
  const [salesPersonList, setSalesPersonList] = useState([])
  const [pricingCategories, setPricingCategories] = useState([])
  const [selectedSalesPerson, setSelectedSalesPerson] = useState(null)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [pricingMode, setPricingMode] = useState('')
  const [pricingModeTouched, setPricingModeTouched] = useState(false)
  const [generationError, setGenerationError] = useState(null)
  const [fileError, setFileError] = useState('')
  const [invoiceProductMarkups, setInvoiceProductMarkups] = useState([])

  useEffect(() => {
    getList(token, { 'user-specific': false }).then((res) => {
      const salesPeople = []
      res.collection.forEach((item) => {
        if (item.data.access.control['sales person'] === true) {
          salesPeople.push({ value: item.raw.id, label: item.data.name })
        }
      })
      setSalesPersonList(salesPeople)
    })
  }, [token])

  useEffect(() => {
    getPricingCategories(token).then((res) => {
      setPricingCategories(res?.categories ?? [])
    })
  }, [token])

  const handleFileChange = (event) => {
    currentFile.current = event.target.files[0]
    setSelectedFile(event.target.files[0])
    setProductResolutions({})
    setFileError('')
    setGenerationError(null)
    setInvoiceProductMarkups([])
  }

  const handleUpload = () => {
    setPricingModeTouched(true)
    if (!pricingMode) return false
    if (!selectedFile) {
      setFileError('Choose an Excel file before generating the invoice.')
      return false
    }

    setGenerating(true)
    setGenerationError(null)
    generate(token, {
      file: selectedFile,
      productResolutions: Object.values(productResolutions),
      pricingMode,
      invoiceProductMarkups,
      ...invoiceData,
    }).then(
      (res) => {
        setGenerating(false)
        if (res?.status) {
          setGenerationError({
            message:
              res.data?.message ??
              'The invoice could not be generated. Check the file and try again.',
            errors: res.data?.errors ?? [],
            customerId: res.data?.customer_id ?? null,
          })
          return false
        }
        setGeneratedInfo(res)
      },
    )
  }

  const handleSubmit = () => {
    if (!window.confirm('Are you sure you want to submit?')) return false
    if (!selectedSalesPerson) {
      alert('Please select a sales person')
      return false
    }

    setLoading(true)
    const { couponContext, ...invoiceData } = generatedInfo
    create(token, {
      'coupon-context': couponContext,
      'customer-id': generatedInfo.customerAccount.id,
      'invoice-id': generatedInfo.invoiceNumber,
      raw: invoiceData,
      'invoice-file': generatedInfo.fileUrl,
      status: 'completed',
      'invoice-date': generatedInfo.orderDate,
      'sales-person-id': selectedSalesPerson.value,
      'sales-person-name': selectedSalesPerson.label,
    }).then((res) => {
      setLoading(false)
      if (res.status && res.status === 404) return false
      alert('Invoice has been created successfully')
      navigate('/invoices')
    })
  }

  return (
    <>
      <ResponsiveAppBar />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        <Stack
          alignItems={{ xs: 'stretch', md: 'center' }}
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography component="h1" fontWeight={700} variant="h4">
              {generatedInfo ? 'Review invoice' : 'Create invoice'}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {generatedInfo
                ? 'Confirm the customer, salesperson, and totals before submitting.'
                : 'Upload the source spreadsheet and choose how prices should be calculated.'}
            </Typography>
          </Box>
          {generatedInfo && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ '& .MuiButton-root': { minHeight: 44 } }}
            >
              <Button
                onClick={() => setGeneratedInfo(null)}
                startIcon={<ArrowBackOutlinedIcon />}
                variant="outlined"
              >
                Start over
              </Button>
              <Button
                onClick={() => window.open(generatedInfo.fileUrl, '_blank')}
                startIcon={<DownloadOutlinedIcon />}
                variant="outlined"
              >
                Download spreadsheet
              </Button>
              <Button
                disabled={loading}
                onClick={handleSubmit}
                startIcon={
                  loading ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null
                }
                variant="contained"
              >
                {loading ? 'Creating invoice…' : 'Create invoice'}
              </Button>
            </Stack>
          )}
        </Stack>

        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            mt: 3,
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {!generatedInfo ? (
            <InvoiceSetupForm
              fileError={fileError}
              generating={generating}
              generationError={generationError}
              handleFileChange={handleFileChange}
              handleUpload={handleUpload}
              invoiceData={invoiceData}
              pricingMode={pricingMode}
              pricingModeTouched={pricingModeTouched}
              pricingCategories={pricingCategories}
              selectedFile={selectedFile}
              setGenerationError={setGenerationError}
              setInvoiceData={setInvoiceData}
              setPricingMode={setPricingMode}
              setPricingModeTouched={setPricingModeTouched}
              token={token}
              invoiceProductMarkups={invoiceProductMarkups}
              setInvoiceProductMarkups={setInvoiceProductMarkups}
              onProductResolved={(line, resolution) => { if (currentFile.current === selectedFile) setProductResolutions(current => ({ ...current, [line]: resolution })) }}
            />
          ) : (
            <Review
              data={generatedInfo}
              salesPersonList={salesPersonList}
              setSelectedSalesPerson={setSelectedSalesPerson}
            />
          )}
        </Paper>
      </Container>
    </>
  )
}

const InvoiceSetupForm = ({
  fileError,
  generating,
  generationError,
  handleFileChange,
  handleUpload,
  invoiceData,
  pricingMode,
  pricingModeTouched,
  pricingCategories,
  selectedFile,
  setGenerationError,
  setInvoiceData,
  setPricingMode,
  setPricingModeTouched,
  token,
  invoiceProductMarkups,
  setInvoiceProductMarkups,
  onProductResolved,
}) => (
  <Box
    component="form"
    onSubmit={(event) => {
      event.preventDefault()
      handleUpload()
    }}
  >
    <Stack spacing={4}>
      <Box>
        <SectionHeader
          description="Choose the original supplier spreadsheet in Excel format."
          step="1"
          title="Upload source invoice"
        />
        <Box
          component="label"
          htmlFor="file-input"
          sx={{
            alignItems: 'center',
            bgcolor: selectedFile ? 'action.selected' : 'action.hover',
            border: 1,
            borderColor: fileError
              ? 'error.main'
              : selectedFile
                ? 'primary.main'
                : 'divider',
            borderRadius: 2,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mt: 2,
            p: { xs: 2.5, sm: 3 },
            transition: 'border-color 160ms ease, background 160ms ease',
            '&:hover': {
              bgcolor: 'action.selected',
              borderColor: 'primary.main',
            },
            '&:focus-within': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'background.paper',
              borderRadius: 2,
              color: 'primary.main',
              display: 'flex',
              flex: '0 0 auto',
              height: 48,
              justifyContent: 'center',
              width: 48,
            }}
          >
            <UploadFileOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography fontWeight={600}>
              {selectedFile ? selectedFile.name : 'Choose an Excel file'}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {selectedFile
                ? 'File selected and ready to process.'
                : 'Accepted formats: .xls and .xlsx'}
            </Typography>
          </Box>
          <Button component="span" sx={{ minHeight: 44 }} variant="outlined">
            {selectedFile ? 'Replace file' : 'Browse files'}
          </Button>
          <input
            accept=".xls,.xlsx"
            id="file-input"
            onChange={handleFileChange}
            style={visuallyHiddenStyles}
            type="file"
          />
        </Box>
        {fileError && (
          <FormHelperText error role="alert" sx={{ ml: 1, mt: 1 }}>
            {fileError}
          </FormHelperText>
        )}
      </Box>

      <Divider />

      <Box>
        <SectionHeader
          description="Select the pricing source for this invoice."
          step="2"
          title="Choose pricing method"
        />
        <FormControl
          component="fieldset"
          error={pricingModeTouched && !pricingMode}
          required
          sx={{ mt: 2, width: '100%' }}
        >
          <FormLabel component="legend" sx={visuallyHiddenStyles}>
            Invoice pricing method
          </FormLabel>
          <RadioGroup
            aria-label="Invoice pricing method"
            name="pricing-mode"
            onChange={(event) => {
              setPricingMode(event.target.value)
              setPricingModeTouched(true)
              setGenerationError(null)
            }}
            value={pricingMode}
          >
            <Grid container spacing={2}>
              <PricingOption
                description="Use column I, with optional invoice-level markup below."
                icon={<EditOutlinedIcon />}
                label="Manual pricing"
                selected={pricingMode === 'manual'}
                value="manual"
              />
              <PricingOption
                description="Apply the saved rate for each of the customer’s product categories."
                icon={<PersonOutlineOutlinedIcon />}
                label="Customer pricing profile"
                selected={pricingMode === 'customer_profile'}
                value="customer_profile"
              />
            </Grid>
          </RadioGroup>
          <FormHelperText sx={{ mt: 1 }}>
            {pricingModeTouched && !pricingMode
              ? 'Choose a pricing method before generating the invoice.'
              : 'This choice applies only to this invoice.'}
          </FormHelperText>
        </FormControl>

        {pricingMode === 'customer_profile' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Saved customer category rates will be used. Column I and manual
            markup values will be ignored.
          </Alert>
        )}

        {pricingMode === 'manual' && (
          <Box
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 2,
              mt: 2,
              p: { xs: 2, sm: 2.5 },
            }}
          >
            <Typography fontWeight={600}>Manual markup</Typography>
            <Typography color="text.secondary" variant="body2">
              Optional values applied in addition to the spreadsheet pricing.
            </Typography>
            <AdjustmentFields
              costLabel="Fixed markup"
              costValue={invoiceData.markupCost}
              percentageLabel="Markup percentage"
              percentageValue={invoiceData.markupPercentage}
              setCost={(value) =>
                setInvoiceData({ ...invoiceData, markupCost: value })
              }
              setPercentage={(value) =>
                setInvoiceData({ ...invoiceData, markupPercentage: value })
              }
            />
          </Box>
        )}
      </Box>

      <Divider />

      <Box>
        <SectionHeader
          description="Add an invoice-level delivery fee, if needed."
          step="3"
          title="Delivery adjustments"
        />
        <AdjustmentFields
          costLabel="Fixed delivery fee"
          costValue={invoiceData.deliveryFeeCost}
          percentageLabel="Delivery fee percentage"
          percentageValue={invoiceData.deliveryFeePercentage}
          setCost={(value) =>
            setInvoiceData({ ...invoiceData, deliveryFeeCost: value })
          }
          setPercentage={(value) =>
            setInvoiceData({ ...invoiceData, deliveryFeePercentage: value })
          }
        />
      </Box>

      {generationError && (
        <InvoiceGenerationError
          categories={pricingCategories}
          error={generationError}
          onProductResolved={onProductResolved}
          token={token}
          invoiceProductMarkups={invoiceProductMarkups}
          setInvoiceProductMarkups={setInvoiceProductMarkups}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          disabled={generating}
          endIcon={generating ? null : <ArrowForwardOutlinedIcon />}
          size="large"
          startIcon={
            generating ? <CircularProgress color="inherit" size={18} /> : null
          }
          type="submit"
          variant="contained"
          sx={{ minHeight: 48 }}
        >
          {generating ? 'Generating invoice…' : generationError ? 'Retry generating invoice' : 'Generate invoice'}
        </Button>
      </Box>
    </Stack>
  </Box>
)

const SectionHeader = ({ step, title, description }) => (
  <Stack alignItems="flex-start" direction="row" spacing={2}>
    <Box
      aria-hidden="true"
      sx={{
        alignItems: 'center',
        bgcolor: 'primary.main',
        borderRadius: '50%',
        color: 'primary.contrastText',
        display: 'flex',
        flex: '0 0 auto',
        fontSize: 14,
        fontWeight: 700,
        height: 32,
        justifyContent: 'center',
        mt: 0.25,
        width: 32,
      }}
    >
      {step}
    </Box>
    <Box>
      <Typography component="h2" fontWeight={650} variant="h6">
        {title}
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {description}
      </Typography>
    </Box>
  </Stack>
)

const PricingOption = ({ value, selected, icon, label, description }) => (
  <Grid item md={6} xs={12}>
    <FormControlLabel
      control={<Radio />}
      label={
        <Stack alignItems="flex-start" direction="row" spacing={1.5}>
          <Box
            sx={{
              color: selected ? 'primary.main' : 'text.secondary',
              mt: 0.25,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography fontWeight={600}>{label}</Typography>
            <Typography color="text.secondary" variant="body2">
              {description}
            </Typography>
          </Box>
        </Stack>
      }
      sx={{
        alignItems: 'flex-start',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        boxSizing: 'border-box',
        height: '100%',
        m: 0,
        p: 2,
        transition: 'border-color 160ms ease, background 160ms ease',
        width: '100%',
        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
        '& .MuiFormControlLabel-label': { flex: 1 },
        '& .MuiRadio-root': { mt: -0.75 },
      }}
      value={value}
    />
  </Grid>
)

const AdjustmentFields = ({
  percentageLabel,
  percentageValue,
  setPercentage,
  costLabel,
  costValue,
  setCost,
}) => (
  <Grid container spacing={2} sx={{ mt: 0.5 }}>
    <Grid item sm={6} xs={12}>
      <TextField
        defaultValue={percentageValue}
        fullWidth
        inputProps={{ min: 0, step: '0.001' }}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
        label={percentageLabel}
        onChange={(event) => setPercentage(event.target.value)}
        type="number"
      />
    </Grid>
    <Grid item sm={6} xs={12}>
      <TextField
        defaultValue={costValue}
        fullWidth
        inputProps={{ min: 0, step: '0.01' }}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        label={costLabel}
        onChange={(event) => setCost(event.target.value)}
        type="number"
      />
    </Grid>
  </Grid>
)

const Review = ({ data, salesPersonList, setSelectedSalesPerson }) => {
  const customerSalesPerson = data?.customerAccount?.['sales-person'] ?? null

  useEffect(() => {
    setSelectedSalesPerson(customerSalesPerson)
  }, [customerSalesPerson, setSelectedSalesPerson])

  return (
    <>
      <table className="table-information">
        <tbody>
          <tr>
            <td className="caption">
              <strong>Pricing Mode: </strong>
            </td>
            <td>
              <Chip
                color={
                  data.pricingMode === 'customer_profile'
                    ? 'success'
                    : 'default'
                }
                label={
                  data.pricingMode === 'customer_profile'
                    ? 'Customer category pricing'
                    : 'Manual pricing'
                }
                size="small"
                variant="outlined"
              />
            </td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Sales Person: </strong>
            </td>
            <td>
              <ReactSelect
                defaultValue={customerSalesPerson}
                options={salesPersonList}
                onChange={(value) => setSelectedSalesPerson(value)}
                placeholder="Sales Person"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    background: '#fff',
                    borderColor: '#9e9e9e',
                    boxShadow: null,
                    height: '55px',
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: '55px',
                    padding: '0 6px',
                  }),
                  input: (provided) => ({ ...provided, margin: '0px' }),
                  indicatorSeparator: () => ({ display: 'none' }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: '55px',
                  }),
                }}
              />
            </td>
          </tr>
          <InformationRow
            label="Account Number"
            value={data.customerAccount['account-number'] ?? ''}
          />
          <InformationRow
            label="Company Name"
            value={data.customerAccount['company-name'] ?? ''}
          />
          <InformationRow
            label="Address Line 1"
            value={data.customerAccount['address-line-1'] ?? ''}
          />
          <InformationRow
            label="Address Line 2"
            value={data.customerAccount['address-line-2'] ?? ''}
          />
          <InformationRow
            label="Phone Number"
            value={data.customerAccount.phone ?? ''}
          />
          <InformationRow
            label="Email Address"
            value={data.customerAccount.email ?? ''}
          />
          <InformationRow
            label="Sub-Total"
            value={`$ ${data.subTotal ?? ''}`}
          />
          <InformationRow
            label="Delivery Fee"
            value={`$ ${data.deliveryFee ?? ''}`}
          />
          <InformationRow label="Tax" value={`$ ${data.tax ?? '0.00'}`} />
          <InformationRow label="Total" value={`$ ${data.grandTotal ?? ''}`} />
        </tbody>
      </table>
      <ProductList itemList={data.itemSummary} pricingMode={data.pricingMode} />
    </>
  )
}

const InformationRow = ({ label, value }) => (
  <tr>
    <td className="caption">
      <strong>{label}: </strong>
    </td>
    <td>{value}</td>
  </tr>
)

const ProductList = ({ itemList, pricingMode }) => {
  const columns = [
    { field: 'UPC', headerName: 'UPC', minWidth: 105, width: 105 },
    { field: 'Item', headerName: 'Item', minWidth: 75, width: 75 },
    {
      field: 'Description',
      headerName: 'Description',
      flex: 1.4,
      minWidth: 160,
    },
    ...(pricingMode === 'customer_profile'
      ? [
          {
            field: 'Category',
            headerName: 'Category',
            flex: 0.9,
            minWidth: 110,
          },
          {
            field: 'Pricing Rule',
            headerName: 'Applied Adjustment',
            minWidth: 115,
            width: 115,
          },
          {
            field: 'Pricing Source',
            headerName: 'Pricing Source',
            minWidth: 160,
            width: 160,
            valueFormatter: ({ value }) => ({
              invoice_product_override: 'Invoice-only product markup',
              customer_product_override: 'Customer product override',
              customer_category: 'Customer category rate',
            })[value] ?? value,
          },
        ]
      : []),
    {
      align: 'right',
      field: 'Price',
      headerAlign: 'right',
      headerName: 'Unconverted Price',
      valueFormatter: ({ value }) => Number(String(value ?? 0).replace(/,/g, '')).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: 95,
      width: 95,
    },
    {
      align: 'right',
      field: 'New Price',
      headerAlign: 'right',
      headerName: 'Converted Price',
      minWidth: 95,
      width: 95,
    },
    {
      align: 'right',
      field: 'Qty',
      headerAlign: 'right',
      headerName: 'Qty',
      minWidth: 55,
      width: 55,
    },
    {
      align: 'center',
      field: 'C/U',
      headerAlign: 'center',
      headerName: 'C/U',
      minWidth: 60,
      width: 60,
    },
    {
      align: 'right',
      field: 'New Total',
      headerAlign: 'right',
      headerName: 'Total',
      minWidth: 100,
      width: 100,
    },
  ]

  return (
    <Box sx={{ mb: 1, mt: 2 }}>
      <DataGrid
        autoHeight
        columnHeaderHeight={64}
        columns={columns}
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        rows={itemList.map((item, index) => ({ id: index, ...item }))}
        sx={{
          borderColor: 'divider',
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
            display: 'flex',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.4,
            py: 1.25,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            lineHeight: 1.25,
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'normal',
          },
        }}
      />
    </Box>
  )
}

const InvoiceGenerationError = ({
  error,
  categories,
  token,
  invoiceProductMarkups,
  setInvoiceProductMarkups,
  onProductResolved,
}) => {
  const errors = Array.isArray(error.errors) ? error.errors : []
  const [customerPricing, setCustomerPricing] = useState(null)

  useEffect(() => {
    if (!error.customerId) return
    getPricing(token, error.customerId).then((response) => {
      if (!response?.status) setCustomerPricing(response)
    })
  }, [error.customerId, token])

  const setInvoiceMarkup = (productId, adjustmentType, adjustmentValue) => {
    setInvoiceProductMarkups((current) => {
      const remaining = current.filter((item) => item.product_id !== productId)
      return adjustmentValue === null
        ? remaining
        : [...remaining, {
            product_id: productId,
            adjustment_type: adjustmentType,
            adjustment_value: Number(adjustmentValue),
          }]
    })
  }

  return (
    <Paper
      elevation={0}
      sx={{ border: 1, borderColor: 'error.light', overflow: 'hidden' }}
    >
      <Alert role="alert" severity="error" sx={{ borderRadius: 0 }}>
        <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
          Invoice pricing needs attention
        </AlertTitle>
        {error.message}
      </Alert>
      {errors.length > 0 && (
        <>
          <TableContainer>
            <Table
              aria-label="Invoice pricing issues"
              size="small"
              sx={{ minWidth: 960 }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Rows</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 240 }}>
                    Product
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Problem</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 340 }}>
                    Resolution
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((item, index) => (
                  <InvoiceErrorRow
                    categories={categories}
                    item={item}
                    key={`${item.source_row ?? item.line}-${item.item}-${index}`}
                    token={token}
                    customerId={error.customerId}
                    pricingRules={customerPricing?.rules ?? []}
                    invoiceMarkup={invoiceProductMarkups.find((markup) => markup.product_id === item.product_id)}
                    onInvoiceMarkup={setInvoiceMarkup}
                    onProductResolved={onProductResolved}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              bgcolor: 'background.default',
              borderTop: 1,
              borderColor: 'divider',
              px: 2,
              py: 1.5,
            }}
          >
            <Typography color="text.secondary" variant="body2">
              Save missing products and categories, then generate the invoice again.
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  )
}

const InvoiceErrorRow = ({
  item,
  categories,
  token,
  customerId,
  pricingRules,
  invoiceMarkup,
  onInvoiceMarkup,
  onProductResolved,
}) => {
  const [savedProduct, setSavedProduct] = useState(null)
  const [assignedCategory, setAssignedCategory] = useState('')
  const category = assignedCategory || item.category
  const categoryUnavailable =
    !category || category.trim().toLowerCase() === 'search'

  return (
    <TableRow
      sx={{
        '&:last-child td': { borderBottom: 0 },
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Chip
          label={item.source_rows?.join(', ') || item.source_row || item.line || '—'}
          size="small"
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Stack alignItems="center" direction="row" spacing={1.5}>
          <ProductThumbnail
            description={item.description}
            image={item.product_image}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={600} variant="body2">
              {item.item || 'Unknown item'}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {item.description || 'No description available'}
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Chip
          color={assignedCategory ? 'success' : 'default'}
          label={categoryUnavailable ? 'N/A' : category}
          size="small"
          variant={assignedCategory ? 'filled' : 'outlined'}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Typography variant="body2">{savedProduct ? "Product linked to this invoice row." : item.message}</Typography>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        {item.type === 'product_not_found' && !savedProduct ? (
          <MissingInvoiceProduct item={item} categories={categories} token={token} onSaved={result => {
            setSavedProduct(result.product)
            setAssignedCategory(result.product.category || '')
            onProductResolved(item.line, result.resolution)
          }} />
        ) : (savedProduct || (item.type === 'product_category_missing' && item.product_id)) ? (
          <>
          {savedProduct && <Alert severity="success">Product linked. Confirm its category and pricing below, then generate again.</Alert>}
          <CategoryResolution
            categories={categories}
            item={savedProduct ? { ...item, product_id: savedProduct.id, category: savedProduct.category } : item}
            onSaved={setAssignedCategory}
            token={token}
            customerId={customerId}
            pricingRules={pricingRules}
            invoiceMarkup={invoiceMarkup}
            onInvoiceMarkup={onInvoiceMarkup}
          />
          </>
        ) : (
          <Typography color="text.secondary" variant="body2">
            {item.type === 'product_not_found'
              ? 'Add or match this product in the catalog first.'
              : 'Update the customer pricing profile for this category.'}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  )
}

const ProductThumbnail = ({ image, description }) => {
  const [imageFailed, setImageFailed] = useState(false)

  if (image && !imageFailed) {
    return (
      <Box
        alt={description ? `${description} product` : 'Product'}
        component="img"
        loading="lazy"
        onError={() => setImageFailed(true)}
        src={image}
        sx={{
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          flex: '0 0 auto',
          height: 52,
          objectFit: 'contain',
          p: 0.5,
          width: 52,
        }}
      />
    )
  }

  return (
    <Box
      aria-label="No product image available"
      role="img"
      sx={{
        alignItems: 'center',
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        color: 'text.disabled',
        display: 'flex',
        flex: '0 0 auto',
        height: 52,
        justifyContent: 'center',
        width: 52,
      }}
    >
      <ImageNotSupportedOutlinedIcon fontSize="small" />
    </Box>
  )
}

const CategoryResolution = ({
  item,
  categories,
  onSaved,
  token,
  customerId,
  pricingRules,
  invoiceMarkup,
  onInvoiceMarkup,
}) => {
  const initialCategory = item.category && categories.includes(item.category) ? item.category : ''
  const [selectedCategory, setSelectedCategory] = useState(initialCategory)
  const [savedCategory, setSavedCategory] = useState(initialCategory)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [pricingChoice, setPricingChoice] = useState('category')
  const [adjustmentType, setAdjustmentType] = useState(
    invoiceMarkup?.adjustment_type || 'percentage',
  )
  const [adjustmentValue, setAdjustmentValue] = useState(
    invoiceMarkup ? String(invoiceMarkup.adjustment_value ?? invoiceMarkup.markup_percentage) : '',
  )
  const [pricingSaved, setPricingSaved] = useState(false)
  const categoryRule = pricingRules.find(
    (rule) => rule.category === savedCategory,
  )

  const handleSave = () => {
    if (!selectedCategory) return

    setSaving(true)
    setSaveError('')
    updateProduct(token, { category: selectedCategory }, item.product_id).then(
      (res) => {
        setSaving(false)
        if (res?.status) {
          setSaveError(
            res.data?.message || 'The category could not be saved. Try again.',
          )
          return
        }

        setSavedCategory(selectedCategory)
        onSaved(selectedCategory)
      },
    )
  }

  const handlePricingSave = () => {
    if (pricingChoice === 'category') {
      onInvoiceMarkup(item.product_id, null, null)
      setPricingSaved(true)
      return
    }
    if (adjustmentValue === '' || Number(adjustmentValue) < 0) return
    setSaving(true)
    setSaveError('')
    saveProductMarkup(token, customerId, item.product_id, adjustmentType, adjustmentValue).then(
      (response) => {
        setSaving(false)
        if (response?.status) {
          setSaveError(
            Object.values(response.data?.errors ?? {}).flat().join(' ') ||
              response.data?.message ||
              'The product override could not be saved.',
          )
          return
        }
        onInvoiceMarkup(item.product_id, null, null)
        setPricingSaved(true)
      },
    )
  }

  return (
    <Box>
      <Stack alignItems="flex-start" direction="row" spacing={1}>
        <TextField
          aria-label={`Category for ${item.description || item.item}`}
          label="Select category"
          onChange={(event) => {
            setSelectedCategory(event.target.value)
            setSaveError('')
          }}
          select
          size="small"
          sx={{ minWidth: 190, '& .MuiInputBase-root': { minHeight: 44 } }}
          value={selectedCategory}
        >
          {categories.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </TextField>
        <Button
          aria-label={`Save category for ${item.description || item.item}`}
          disabled={
            !selectedCategory || saving || selectedCategory === savedCategory
          }
          onClick={handleSave}
          startIcon={
            saving ? (
              <CircularProgress color="inherit" size={16} />
            ) : selectedCategory === savedCategory && savedCategory ? (
              <CheckCircleOutlineIcon />
            ) : (
              <SaveOutlinedIcon />
            )
          }
          sx={{ minHeight: 44 }}
          variant="outlined"
        >
          {saving
            ? 'Saving…'
            : selectedCategory === savedCategory && savedCategory
              ? 'Saved'
              : 'Save'}
        </Button>
      </Stack>
      {savedCategory && !saveError && (
        <Box sx={{ mt: 1.5 }}>
          {!categoryRule ? (
            <Alert severity="warning">
              Configure {savedCategory} pricing in this customer’s profile before regenerating.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              <Alert severity="info">
                Category rate: {categoryRule.adjustment_type === 'percentage'
                  ? `${Number(categoryRule.adjustment_value)}%`
                  : `$${Number(categoryRule.adjustment_value).toFixed(2)} per item`}
              </Alert>
              <FormControl component="fieldset">
                <FormLabel component="legend">Pricing for this product</FormLabel>
                <RadioGroup
                  onChange={(event) => {
                    setPricingChoice(event.target.value)
                    setPricingSaved(false)
                  }}
                  value={pricingChoice}
                >
                  <FormControlLabel control={<Radio size="small" />} label="Use category rate" value="category" />
                  <FormControlLabel control={<Radio size="small" />} label="Save as customer product override" value="permanent" />
                </RadioGroup>
              </FormControl>
              {pricingChoice !== 'category' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    fullWidth
                    label="Adjustment type"
                    onChange={(event) => {
                      setAdjustmentType(event.target.value)
                      setPricingSaved(false)
                    }}
                    select
                    size="small"
                    value={adjustmentType}
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="fixed_amount">Fixed cost</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    helperText={adjustmentType === 'percentage' ? 'Percentage of base price' : 'Added per item'}
                    inputProps={{ min: 0, step: adjustmentType === 'percentage' ? '0.001' : '0.01' }}
                    InputProps={{
                      startAdornment: adjustmentType === 'fixed_amount' ? <InputAdornment position="start">$</InputAdornment> : null,
                      endAdornment: adjustmentType === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : null,
                    }}
                    label="Adjustment value"
                    onChange={(event) => {
                      setAdjustmentValue(event.target.value)
                      setPricingSaved(false)
                    }}
                    size="small"
                    type="number"
                    value={adjustmentValue}
                  />
                </Stack>
              )}
              <Button disabled={saving || pricingSaved || (pricingChoice !== 'category' && adjustmentValue === '')} onClick={handlePricingSave} size="small" variant="outlined">
                {pricingSaved ? 'Pricing saved' : 'Save pricing choice'}
              </Button>
            </Stack>
          )}
        </Box>
      )}
      {saveError && (
        <Typography
          color="error.main"
          role="alert"
          sx={{ mt: 0.75 }}
          variant="caption"
        >
          {saveError}
        </Typography>
      )}
    </Box>
  )
}

const visuallyHiddenStyles = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
}

export default App


export const MissingInvoiceProduct = ({ item, categories, token, onSaved }) => {
  const { access } = useContext(UserContext)
  const [draft, setDraft] = useState({ item_number: item.item || '', name: item.description || '', upc: item.upc || '', category: '' })
  const [match, setMatch] = useState(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const change = key => event => { setDraft(current => ({ ...current, [key]: event.target.value })); setMatch(null); setError('') }
  const save = async (confirm = false) => {
    if (!draft.item_number.trim() || ['coupon', 'n/a', 'unknown'].includes(draft.item_number.trim().toLowerCase()) || !draft.name.trim() || !draft.category) {
      setError('Enter a real item number, product name, and category.'); return
    }
    setSaving(true); setError('')
    try {
      const result = await resolveProduct(token, { ...draft, context: item.context, ...(confirm ? { confirm_product_id: match.id } : {}) })
      if (result.confirmation_required) setMatch(result.product)
      else onSaved(result)
    } catch (failure) {
      setError(Object.values(failure.response?.data?.errors || {}).flat().join(' ') || failure.response?.data?.message || 'Unable to save product. Please try again.')
    } finally { setSaving(false) }
  }
  if (!access?.control?.products || !access?.control?.invoices) return <Typography variant="body2">Invoice and product access are required to add this product.</Typography>
  return <Stack spacing={1.5} sx={{ minWidth: 260 }}>
    <Typography variant="subtitle2">Add product to catalog</Typography>
    <TextField label="Item number" size="small" value={draft.item_number} onChange={change('item_number')} disabled={saving} helperText={draft.item_number.toLowerCase() === 'coupon' ? 'Replace Coupon with the real product item number.' : ''} />
    <TextField label="Product name" size="small" value={draft.name} onChange={change('name')} disabled={saving} />
    <TextField label="UPC (optional)" size="small" value={draft.upc} onChange={change('upc')} disabled={saving} />
    <TextField select label="Category" size="small" value={draft.category} onChange={change('category')} disabled={saving}>
      {categories.filter(category => category.trim().toLowerCase() !== 'search').map(category => <MenuItem key={category} value={category}>{category}</MenuItem>)}
    </TextField>
    <Typography variant="caption">Saved products are immediately available for ordering.</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {match && <Alert severity="info">Existing product: {match['item-number']} — {match.name} ({match.category || 'No category'}). Confirm to use it without changing its details.</Alert>}
    <Button type="button" variant="contained" disabled={saving} onClick={() => save(Boolean(match))}>{saving ? 'Saving…' : match ? 'Use existing product' : 'Save product'}</Button>
  </Stack>
}
